import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Home,
  Search,
  ArrowRight,
  Loader2,
  ArrowLeft,
  ImagePlus,
  Building2,
  Tag,
  MapPin,
  Maximize2,
  Clock,
  Lock,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_8f20c47c-6306-4c92-aa61-f6db9e4c740d/artifacts/cc7z0gyj_ChatGPT%20Image%20Apr%2026%2C%202026%2C%2010_37_30%20AM.png";

const formatINR = (n) => {
  const num = Math.round(Number(n) || 0);
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(num % 10000000 === 0 ? 0 : 2)} Cr`;
  if (num >= 100000) return `₹${(num / 100000).toFixed(num % 100000 === 0 ? 0 : 2)} L`;
  return `₹${num.toLocaleString("en-IN")}`;
};

export default function OwnerPortal() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { isAuthed, user, requireAuth } = useAuth();
  const tab = params.get("tab") === "search" ? "search" : "sell";

  useEffect(() => {
    document.title = "Owner Portal · Zumroo";
  }, []);

  useEffect(() => {
    if (!isAuthed) requireAuth({ redirectTo: "/owner" });
  }, [isAuthed, requireAuth]);

  if (!isAuthed) {
    return (
      <div className="bg-[#F7F6F2] min-h-screen">
        <Navbar logoUrl={LOGO_URL} content={{ play_store_url: "#" }} transparent={false} />
        <div className="container-z pt-32 pb-20 text-center text-[#5C6B62]">
          Sign in to access the owner portal.
        </div>
      </div>
    );
  }

  const setTab = (t) => setParams({ tab: t });

  return (
    <div className="bg-[#F7F6F2] min-h-screen">
      <Navbar logoUrl={LOGO_URL} content={{ play_store_url: "#" }} transparent={false} />

      <main className="container-z pt-20 sm:pt-24 pb-16">
        <Link to="/" className="flex items-center gap-1 text-sm text-[#5C6B62] hover:text-[#1A231E] mb-5">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <div className="label-mono mb-3">Owner Portal</div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1A231E]">
          Hi {user?.name?.split(" ")[0] || "there"}, what would you like to do?
        </h1>
        <p className="mt-3 text-base text-[#5C6B62] max-w-xl">
          Sell or rent out your property in minutes — or browse the listings you've already posted.
        </p>

        {/* Two big pathway cards */}
        <div className="grid sm:grid-cols-2 gap-5 mt-8">
          <button
            onClick={() => setTab("sell")}
            className={`text-left p-6 rounded-2xl border transition relative overflow-hidden ${
              tab === "sell" ? "bg-[#1E3F33] border-[#1E3F33] text-white" : "bg-white border-[#E4E1D9] hover:border-[#1E3F33]"
            }`}
            data-testid="owner-tab-sell"
          >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${tab === "sell" ? "bg-white/15" : "bg-[#FBEFE9] text-[#C86646]"}`}>
              <Home className={`w-6 h-6 ${tab === "sell" ? "text-white" : ""}`} />
            </div>
            <h3 className={`mt-4 font-display text-2xl font-bold ${tab === "sell" ? "text-white" : "text-[#1A231E]"}`}>
              Sell Property
            </h3>
            <p className={`mt-1 text-sm ${tab === "sell" ? "text-white/75" : "text-[#5C6B62]"}`}>
              Create a new listing. Free for owners — get verified buyer leads.
            </p>
          </button>

          <button
            onClick={() => setTab("search")}
            className={`text-left p-6 rounded-2xl border transition relative overflow-hidden ${
              tab === "search" ? "bg-[#1E3F33] border-[#1E3F33] text-white" : "bg-white border-[#E4E1D9] hover:border-[#1E3F33]"
            }`}
            data-testid="owner-tab-search"
          >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${tab === "search" ? "bg-white/15" : "bg-[#EAF1ED] text-[#1E3F33]"}`}>
              <Search className={`w-6 h-6 ${tab === "search" ? "text-white" : ""}`} />
            </div>
            <h3 className={`mt-4 font-display text-2xl font-bold ${tab === "search" ? "text-white" : "text-[#1A231E]"}`}>
              Search Your Property
            </h3>
            <p className={`mt-1 text-sm ${tab === "search" ? "text-white/75" : "text-[#5C6B62]"}`}>
              View, edit and track your existing listings &amp; matching buyer requests.
            </p>
          </button>
        </div>

        <div className="mt-10">{tab === "sell" ? <SellForm /> : <MyListings />}</div>
      </main>

      <Footer logoUrl={LOGO_URL} content={{ contact_phone: "+91 98765 43210", contact_email: "hello@zumroo.in" }} />
    </div>
  );
}

// =========== Sell form ===========
function SellForm() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    listing_type: "Sale",
    property_type: "Apartment",
    bedrooms: "2",
    city: "Pune",
    locality: "",
    society_name: "",
    address: "",
    price: "",
    area_sqft: "",
    description: "",
    contact_name: "",
    contact_phone: "",
    contact_email: "",
  });
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.locality.trim()) return toast.error("Locality is required");
    if (!form.price || Number(form.price) <= 0) return toast.error("Enter a valid price");
    if (!form.area_sqft || Number(form.area_sqft) <= 0) return toast.error("Enter the area in sq ft");
    if (!form.contact_name.trim()) return toast.error("Contact name is required");
    if (!/^[0-9+\-\s]{8,15}$/.test(form.contact_phone.trim())) return toast.error("Enter a valid contact phone");
    try {
      setSubmitting(true);
      const payload = {
        ...form,
        bedrooms: form.bedrooms ? Number(form.bedrooms) : null,
        price: Number(form.price),
        area_sqft: Number(form.area_sqft),
      };
      const { data } = await api.post("/listings", payload);
      toast.success("Listing submitted! It will be live after approval.");
      navigate(`/listing/${data.id}`);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.form
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      onSubmit={onSubmit}
      className="zcard p-6 sm:p-8 grid md:grid-cols-2 gap-5"
      data-testid="sell-form"
    >
      <Field label="Listing type">
        <select value={form.listing_type} onChange={update("listing_type")} className="z-input" data-testid="sell-listing-type">
          <option value="Sale">For Sale</option>
          <option value="Rent">For Rent</option>
        </select>
      </Field>
      <Field label="Property type">
        <select value={form.property_type} onChange={update("property_type")} className="z-input" data-testid="sell-property-type">
          <option>Apartment</option>
          <option>Independent House</option>
          <option>Villa</option>
          <option>Plot</option>
          <option>Commercial</option>
        </select>
      </Field>
      <Field label="Bedrooms (BHK)">
        <input value={form.bedrooms} onChange={update("bedrooms")} type="number" min="0" placeholder="2" className="z-input" data-testid="sell-bedrooms" />
      </Field>
      <Field label="City">
        <input value={form.city} onChange={update("city")} className="z-input" data-testid="sell-city" />
      </Field>
      <Field label="Locality *">
        <input value={form.locality} onChange={update("locality")} placeholder="e.g. Kothrud" required className="z-input" data-testid="sell-locality" />
      </Field>
      <Field label="Society name (optional)">
        <input value={form.society_name} onChange={update("society_name")} placeholder="e.g. Sai Residency" className="z-input" data-testid="sell-society" />
      </Field>
      <Field label="Price (₹) *">
        <input type="number" value={form.price} onChange={update("price")} placeholder="8500000" required className="z-input" data-testid="sell-price" />
      </Field>
      <Field label="Area (sqft) *">
        <input type="number" value={form.area_sqft} onChange={update("area_sqft")} placeholder="920" required className="z-input" data-testid="sell-area" />
      </Field>
      <Field label="Address" className="md:col-span-2">
        <input value={form.address} onChange={update("address")} placeholder="Street, landmark…" className="z-input" data-testid="sell-address" />
      </Field>
      <Field label="Description" className="md:col-span-2">
        <textarea value={form.description} onChange={update("description")} rows={3} placeholder="Highlights, parking, floor, etc." className="z-input resize-none" data-testid="sell-description" />
      </Field>
      <Field label="Contact name *">
        <input value={form.contact_name} onChange={update("contact_name")} required className="z-input" data-testid="sell-contact-name" />
      </Field>
      <Field label="Contact phone *">
        <input value={form.contact_phone} onChange={update("contact_phone")} placeholder="9876543210" required className="z-input" data-testid="sell-contact-phone" />
      </Field>
      <Field label="Contact email (optional)" className="md:col-span-2">
        <input type="email" value={form.contact_email} onChange={update("contact_email")} className="z-input" data-testid="sell-contact-email" />
      </Field>
      <div className="md:col-span-2 flex flex-wrap items-center gap-3 pt-2">
        <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-50" data-testid="sell-submit-btn">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
          {submitting ? "Posting…" : "Post listing"}
          <ArrowRight className="w-4 h-4" />
        </button>
        <span className="text-xs text-[#5C6B62]">Your listing will be reviewed and approved within 24 hours.</span>
      </div>
      <style>{`.z-input{width:100%;padding:.75rem 1rem;border-radius:.75rem;border:1px solid #E4E1D9;background:#fff;outline:none}.z-input:focus{border-color:#1E3F33;box-shadow:0 0 0 3px #EAF1ED}`}</style>
    </motion.form>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs font-bold text-[#1A231E] mb-1.5 uppercase tracking-wide">{label}</span>
      {children}
    </label>
  );
}

// =========== My Listings ===========
function MyListings() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/listings/mine");
        const list = Array.isArray(data) ? data : data.items || [];
        setItems(list);
      } catch (e) { toast.error(formatErr(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((l) => `${l.locality || ""} ${l.society_name || ""} ${l.address || ""} ${l.property_type || ""}`.toLowerCase().includes(q));
  }, [items, query]);

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between mb-5">
        <div className="relative flex-1 w-full sm:w-auto sm:max-w-md">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#5C6B62]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search your listings…"
            className="w-full pl-11 pr-4 py-3 rounded-xl border border-[#E4E1D9] bg-white focus:border-[#1E3F33] focus:ring-2 focus:ring-[#EAF1ED] outline-none"
            data-testid="my-listings-search"
          />
        </div>
        <button onClick={() => navigate("/owner?tab=sell")} className="btn-primary text-sm" data-testid="my-listings-create-btn">
          <Home className="w-4 h-4" /> New listing
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {loading && (
          <div className="col-span-full flex items-center gap-2 text-[#5C6B62]">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="col-span-full zcard p-10 text-center text-[#5C6B62]">
            No listings yet. Click <b>Sell Property</b> above to post your first one.
          </div>
        )}
        {filtered.map((l) => (
          <Link key={l.id} to={`/listing/${l.id}`} className="zcard overflow-hidden block" data-testid={`my-listing-${l.id}`}>
            <div className="relative">
              {l.images?.[0] ? (
                <img src={l.images[0]} alt="" className="w-full h-40 object-cover" />
              ) : (
                <div className="w-full h-40 bg-[#EAF1ED] flex items-center justify-center text-[#1E3F33]"><Building2 className="w-8 h-8" /></div>
              )}
              <span className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase ${
                l.status === "approved" ? "bg-[#2D6A4F] text-white"
                  : l.status === "rejected" ? "bg-[#B23A48] text-white"
                  : "bg-[#E9C46A] text-[#1A231E]"
              }`}>{l.status || "pending"}</span>
              {l.human_id && (
                <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-white/90 text-[10px] font-bold tracking-wider text-[#1E3F33]">{l.human_id}</span>
              )}
            </div>
            <div className="p-4">
              <div className="font-display text-xl font-bold text-[#1A231E] tracking-tight">
                {formatINR(l.price)}{l.listing_type === "Rent" && <span className="text-xs text-[#5C6B62] font-medium">/mo</span>}
              </div>
              <div className="mt-1 text-sm text-[#1A231E] truncate">
                {l.property_type}{l.bedrooms ? ` · ${l.bedrooms} BHK` : ""} in {l.locality}
              </div>
              <div className="mt-2 flex items-center gap-2 text-xs text-[#5C6B62]">
                <Maximize2 className="w-3 h-3" /> {l.area_sqft} sqft
                <span className="w-1 h-1 rounded-full bg-[#E4E1D9]" />
                <Clock className="w-3 h-3" /> {l.created_at ? new Date(l.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—"}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </motion.div>
  );
}
